import React, { useEffect, useState } from "react";
import { AlertTriangle, Percent, Landmark, Clock, TrendingUp, ClipboardList, ShieldCheck, PackagePlus, CreditCard, MessageSquareWarning } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import client from "@/lib/api";

const FSTATUS = { pending: "قيد الانتظار", payment_confirmed: "تم تأكيد الدفع", confirmed: "مؤكّد", processing: "قيد التجهيز", shipped: "قيد الشحن", delivered: "تم التسليم", cancelled: "ملغي", disputed: "نزاع" };

const KPI = ({ icon: Icon, label, value, sub, subColor, danger }) => (
  <div className="rounded-2xl p-3.5" style={{ background: "var(--k-surface)", border: `1px solid ${danger ? "var(--k-ruby)" : "var(--k-line)"}` }}>
    <div className="flex items-start justify-between mb-1.5">
      <span className="text-[11px] font-semibold" style={{ color: "var(--k-ink-soft)" }}>{label}</span>
      <Icon size={16} style={{ color: danger ? "var(--k-ruby)" : "var(--k-gold-deep)" }} />
    </div>
    <div className="font-num text-[20px] font-black" style={{ color: danger ? "var(--k-ruby)" : "var(--k-ink)" }}>{value}</div>
    {sub && <div className="text-[10.5px] mt-0.5 font-bold" style={{ color: subColor || "var(--k-emerald)" }}>{sub}</div>}
  </div>
);

export default function AdminOverview({ onSelect }) {
  const [d, setD] = useState(null);
  useEffect(() => { client.get("/admin/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-8 text-center" style={{ color: "var(--k-ink-soft)" }}>جارٍ التحميل…</div>;

  const A = d.actions;
  const actionItems = [
    { k: "verification", icon: ShieldCheck, label: "توثيق تجار", n: A.merchantsPending },
    { k: "orders", icon: ClipboardList, label: "طلبات جديدة", n: A.ordersNew },
    { k: "products", icon: PackagePlus, label: "مخزون منخفض", n: A.lowStock },
    { k: "payments", icon: CreditCard, label: "دفعات معلقة", n: A.paymentsPending },
    { k: "disputes", icon: MessageSquareWarning, label: "نزاعات مفتوحة", n: A.disputesOpen },
  ];

  return (
    <div className="p-4" dir="rtl">
      <h1 className="font-head text-[22px] font-black">لوحة تحكم كزيوني</h1>
      <p className="text-[12.5px] mb-4" style={{ color: "var(--k-ink-soft)" }}>نظرة عامة على نشاط المنصة</p>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5 mb-4">
        <KPI icon={AlertTriangle} danger label="الطلبات المتنازع عليها" value={d.kpi.disputed} sub="نزاع مفتوح" subColor="var(--k-ruby)" />
        <KPI icon={Percent} label="العمولات" value={`${d.kpi.commissionTotal} د.ل`} sub="إجمالي محصّل" />
        <KPI icon={Landmark} label="مستحقات التجار" value={`${d.kpi.netPayout} د.ل`} sub="صافي" />
        <KPI icon={Clock} label="المبيعات قيد التسوية" value={`${d.kpi.settling} د.ل`} sub="بانتظار الدفع" subColor="var(--k-gold-deep)" />
        <KPI icon={TrendingUp} label="المبيعات الإجمالية" value={`${d.kpi.salesTotal} د.ل`} sub="مدفوعة" />
        <KPI icon={ClipboardList} label="إجمالي الطلبات" value={d.kpi.ordersCount} sub="كل الطلبات" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
        {/* Recent orders */}
        <div className="xl:col-span-2 rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
          <div className="flex justify-between items-center mb-3">
            <h2 className="font-head text-[15px] font-bold">الطلبات الأخيرة</h2>
            <button onClick={() => onSelect("orders")} className="k-tap text-[11.5px] font-bold bg-transparent border-0" style={{ color: "var(--k-gold-deep)" }}>عرض الكل ›</button>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-right" style={{ minWidth: 520 }}>
              <thead><tr className="text-[10.5px]" style={{ color: "var(--k-ink-soft)" }}>
                <th className="pb-2 font-semibold">رقم الطلب</th><th className="pb-2 font-semibold">المشتري</th><th className="pb-2 font-semibold">البائع</th><th className="pb-2 font-semibold">المبلغ</th><th className="pb-2 font-semibold">الحالة</th><th className="pb-2 font-semibold">التاريخ</th>
              </tr></thead>
              <tbody>
                {d.recentOrders.map((o) => (
                  <tr key={o.orderId} className="text-[11.5px]" style={{ borderTop: "1px solid var(--k-line)" }}>
                    <td className="py-2 font-num font-bold">{o.orderId}</td>
                    <td className="py-2 truncate" style={{ maxWidth: 120 }}>{o.buyer}</td>
                    <td className="py-2 truncate" style={{ maxWidth: 120, color: "var(--k-ink-soft)" }}>{o.seller}</td>
                    <td className="py-2 font-num">{o.total} د.ل</td>
                    <td className="py-2"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: "var(--k-gold-tint)", color: "var(--k-gold-deep)" }}>{o.statusLabel}</span></td>
                    <td className="py-2 font-num text-[10px]" style={{ color: "var(--k-ink-soft)" }}>{(o.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions + top products */}
        <div className="space-y-3">
          <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <h2 className="font-head text-[15px] font-bold mb-2.5">تحتاج إلى إجراء</h2>
            {actionItems.map((a, i) => {
              const Icon = a.icon;
              return (
                <button key={i} data-testid={`action-${a.label}`} onClick={() => onSelect(a.k)} className="k-tap w-full flex items-center gap-2 py-2 border-0 bg-transparent text-right" style={{ borderTop: i ? "1px solid var(--k-line)" : "none" }}>
                  <span className="font-num text-[12px] font-bold text-white flex items-center justify-center rounded-lg shrink-0" style={{ minWidth: 26, height: 24, background: a.n > 0 ? "var(--k-gold)" : "var(--k-line)", color: a.n > 0 ? "var(--k-ink)" : "var(--k-ink-soft)" }}>{a.n}</span>
                  <Icon size={15} style={{ color: "var(--k-ink-soft)" }} />
                  <span className="text-[12.5px] flex-1">{a.label}</span>
                </button>
              );
            })}
          </div>
          <div className="rounded-2xl p-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
            <h2 className="font-head text-[15px] font-bold mb-2.5">أعلى المنتجات طلباً</h2>
            {d.topProducts.length === 0 ? <p className="text-[12px]" style={{ color: "var(--k-ink-soft)" }}>لا يوجد</p> : d.topProducts.map((p, i) => (
              <div key={i} className="flex justify-between items-center py-1.5 text-[12.5px]" style={{ borderTop: i ? "1px solid var(--k-line)" : "none" }}>
                <span className="truncate" style={{ maxWidth: 160 }}>{p.name}</span>
                <span className="font-num font-bold" style={{ color: "var(--k-gold-deep)" }}>{p.qty} طلب</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sales chart */}
      <div className="rounded-2xl p-4 mb-4" style={{ background: "var(--k-surface)", border: "1px solid var(--k-line)" }}>
        <h2 className="font-head text-[15px] font-bold mb-2">المبيعات خلال 7 أيام</h2>
        <div style={{ width: "100%", height: 190 }}>
          <ResponsiveContainer>
            <LineChart data={d.salesSeries.map((s) => ({ day: s.day.slice(5), value: s.value }))} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--k-line)" />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "var(--k-ink-soft)" }} />
              <YAxis tick={{ fontSize: 10, fill: "var(--k-ink-soft)" }} width={48} />
              <Tooltip formatter={(v) => [`${v} د.ل`, "المبيعات"]} contentStyle={{ fontFamily: "Tajawal", fontSize: 12, direction: "rtl" }} />
              <Line type="monotone" dataKey="value" stroke="var(--k-gold)" strokeWidth={2.5} dot={{ r: 3, fill: "var(--k-gold-deep)" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
